import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import OriginPage from './components/OriginPage';
import SignupFlow from './components/SignupFlow';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GiveGift from './components/GiveGift.jsx';
import PaymentSuccess from './components/PaymentSuccess.jsx';
import Redeem from './components/Redeem.jsx';
import ChooseYourTopic from './components/ChooseYourTopic.jsx';

function App() {
  // Used when there has been a successful payment.
  const [referenceNumber, setReferenceNumber] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refNumber = urlParams.get('reference_number');
    if (refNumber) {
      return refNumber;
    }
    return '';
  });
  
  // Fetch CSRF token when component mounts
  useEffect(() => {
    // Get CSRF token on component mount
    fetch('/api/get_csrf/', {
        credentials: 'include'  // Important for cookies
    })
    .then(response => {
        if (!response.ok) {
            throw new Error('Failed to get CSRF token');
        }
        return response.json();
    })
    .then(data => {
        console.log('CSRF token set successfully');
    })
    .catch(error => {
        console.error('Error setting CSRF token:', error);
    });
  }, []);
  
  // Initialize state from URL path
  const [currentPage, setCurrentPage] = useState(() => {
    // Get the initial page from the URL path
    return getPageFromPath(window.location.pathname);
  });

  // Function to determine page from URL path
  function getPageFromPath(path) {
    if (path === '/payment_success') return 'payment-success';
    if (path === '/redeem') return 'redeem';
    if (path === '/choose_your_topic') return 'choose-your-topic';
    if (path === '/give_gift') return 'give-gift';
    if (path === '/signup') return 'signup';
    return 'home';
  }

  // Listen for browser navigation events (back/forward buttons)
  useEffect(() => {
    const handlePopState = (event) => {
      // Update the current page based on the URL when back/forward buttons are clicked
      const newPage = getPageFromPath(window.location.pathname);
      setCurrentPage(newPage);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update browser history when page changes
  useEffect(() => {
    // Map the current page to a URL path
    let path = '/';
    if (currentPage === 'payment-success') path = '/payment_success';
    if (currentPage === 'redeem') path = '/redeem';
    if (currentPage === 'choose-your-topic') path = '/choose_your_topic';
    if (currentPage === 'give-gift') path = '/give_gift';
    if (currentPage === 'signup') path = '/signup';
    
    // Only push a new history state if the URL is different
    if (window.location.pathname !== path) {
      window.history.pushState({ page: currentPage }, '', path);
    }
  }, [currentPage]);
  
  const navigateToSignup = () => {
    setCurrentPage('signup');
  };

  const navigateToRedeem = () => {
    setCurrentPage('redeem');
  };

  const navigateToGiveGift = () => {
    setCurrentPage('give-gift');
  };

  const navigateToChooseYourTopic = () => {
    setCurrentPage('choose-your-topic');
  };

  const handleGiveGift = (e) => {
    e.preventDefault();
    navigateToGiveGift();
  };

  const handleRedeem = (e) => {
    e.preventDefault();
    navigateToRedeem();
  };

  const navigateToPaymentSuccess = (referenceNumber) => {
    setReferenceNumber(referenceNumber);
    setCurrentPage('payment-success');
  };

  function getActivePage() {
    if (currentPage === 'signup') {
      return <SignupFlow />
    } else if (currentPage === 'give-gift') {
      return (
        <GiveGift 
          navigateToPaymentSuccess={navigateToPaymentSuccess}
        />
      )
    } else if (currentPage === 'payment-success') {
      return (
        <PaymentSuccess 
          referenceNumber={referenceNumber}
        />
      )
    } else if (currentPage === 'redeem') {
      return (
        <Redeem 
          referenceNumber={referenceNumber}
          goHome={goHome}
        />
      )
    } else if (currentPage === 'choose-your-topic') {
      return (
        <ChooseYourTopic />
      )
    } else {
      return (
        <OriginPage 
          navigateToSignup={navigateToSignup}
          navigateToGiveGift={navigateToGiveGift}
        />
      )
    }
  }

  function goHome() {
    if (currentPage !== 'home') {
      setCurrentPage('home');
    }
  }
  
  return (
    <>
      <Header 
        homeLink={goHome} 
        showSiteLinks={currentPage === 'home'} 
        onGiveGift={handleGiveGift} 
        handleRedeem={handleRedeem}
      />
      <div className="main-container" style={{ position: 'relative' }}>
        <AnimatePresence mode="wait">
            <motion.div
                key={currentPage}
                initial={{ opacity: 0}}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0}}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
              {getActivePage()}
            </motion.div>
        </AnimatePresence>
      </div>
      <Footer
        navigationFunctions={{
          "home": goHome,
          "signup": navigateToSignup,
          "give-gift": navigateToGiveGift,
          "redeem": navigateToRedeem,
          "choose-your-topic": navigateToChooseYourTopic,
        }}
      />
    </>
  );
}

export default App;
